import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { apiService } from "../../../services/api";
import { AnexoEmpenhoDetalhado } from "../../../types/anexos_empenhos";
import { Empenho, EmpenhoFormatado } from "../../../types/empenhos";
import ModalDocumentoEmpenho from "./ModalDocumentoEmpenho";

interface DocumentoEmpenho {
    id: number;
    id_empenho: number;
    numero_empenho?: string;
    titulo: string;
    data_upload: string;
    tipo_documento: string;
    nome_arquivo: string;
    usuario_nome?: string;
    arquivos?: Array<{
        nome_original: string;
        nome_salvo: string;
        tipo: string;
        tamanho: number;
        caminho: string;
    }> | string;
}

interface DocumentosEmpenhoProps {
    contratoId: number;
    formatDate: (dateString: string) => string;
    onUpdateComplete: () => void;
}

const DocumentosEmpenho: React.FC<DocumentosEmpenhoProps> = ({
    contratoId,
    formatDate,
    onUpdateComplete
}) => {
    const [documentos, setDocumentos] = useState<DocumentoEmpenho[]>([]);
    const [empenhos, setEmpenhos] = useState<EmpenhoFormatado[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedDocumento, setSelectedDocumento] = useState<DocumentoEmpenho | null>(null);

    // Função para formatar o tamanho do arquivo
    const formatFileSize = (bytes: number): string => {
        if (!bytes || bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Função para determinar o ícone baseado no tipo do arquivo
    const getFileIcon = (tipo: string): string => {
        if (!tipo) return "file";

        if (tipo.includes('image')) return 'photo';
        if (tipo.includes('pdf')) return 'file-text';
        if (tipo.includes('spreadsheet') || tipo.includes('excel')) return 'table';
        if (tipo.includes('word') || tipo.includes('document')) return 'file-text';
        if (tipo.includes('zip') || tipo.includes('rar')) return 'archive';
        return 'file';
    };

    // Função para processar os documentos de empenho preservando os arquivos como array
    const processarAnexosEmpenho = (documentosEmpenho: DocumentoEmpenho[]) => {
        return documentosEmpenho.map(doc => {
            // Se o documento tem a propriedade arquivos como string JSON
            if (doc.arquivos && typeof doc.arquivos === 'string') {
                try {
                    // Converter a string JSON para um array de objetos
                    const arquivosArray = JSON.parse(doc.arquivos);
                    return {
                        ...doc,
                        arquivos: arquivosArray
                    };
                } catch (error) {
                    console.error("Erro ao processar arquivos:", error);
                    return {
                        ...doc,
                        arquivos: []
                    };
                }
            }
            return doc;
        });
    };

    // Carregar documentos e empenhos
    const loadData = async () => {
        try {
            setLoading(true);

            // Primeiro vamos carregar os empenhos para ter o mapeamento
            const empenhosRes = await apiService.getEmpenhos({ id_contrato: contratoId });

            if (empenhosRes.success && empenhosRes.data) {
                const empenhosFormatados = empenhosRes.data.map((empenho: Empenho) => ({
                    ...empenho,
                    numero_empenho: empenho.codigo_empenho || `EMP-${empenho.id}`,
                    status: empenho.situacao
                }));
                setEmpenhos(empenhosFormatados);

                // Agora vamos buscar os documentos para cada empenho
                const todosDocumentos: DocumentoEmpenho[] = [];

                for (const empenho of empenhosFormatados) {
                    try {
                        const anexosRes = await apiService.getAnexosEmpenho({ id_empenho: empenho.id });

                        if (anexosRes.success && anexosRes.data && anexosRes.data.length > 0) {
                            const docsFormatados = anexosRes.data.map((anexo: AnexoEmpenhoDetalhado) => ({
                                id: anexo.id,
                                id_empenho: empenho.id,
                                numero_empenho: empenho.codigo_empenho || `EMP-${empenho.id}`,
                                titulo: anexo.titulo || 'Documento sem título',
                                data_upload: anexo.data_upload || new Date().toISOString(),
                                tipo_documento: anexo.tipo_documento || 'Documento',
                                nome_arquivo: anexo.titulo || `anexo-${anexo.id}.pdf`,
                                usuario_nome: anexo.usuario_nome || 'Usuário',
                                arquivos: anexo.arquivos // Mantendo a string JSON dos arquivos
                            }));
                            todosDocumentos.push(...docsFormatados);
                        }
                    } catch (error) {
                        console.error(`Erro ao carregar documentos do empenho ${empenho.id}:`, error);
                    }
                }

                // Processar os documentos para converter as strings JSON em arrays
                const documentosProcessados = processarAnexosEmpenho(todosDocumentos);
                setDocumentos(documentosProcessados);
            }
        } catch (error) {
            console.error("Erro ao carregar dados:", error);
            toast.error("Erro ao carregar documentos");
        } finally {
            setLoading(false);
        }
    };

    // Carregar dados ao montar o componente
    useEffect(() => {
        loadData();
    }, [contratoId]);

    // Funções para manipular documentos
    const handleAddDocumento = () => {
        setSelectedDocumento(null);
        setShowAddModal(true);
    };

    const handleEditDocumento = (documento: DocumentoEmpenho) => {
        setSelectedDocumento(documento);
        setShowEditModal(true);
    };

    const handleDeleteDocumento = (documento: DocumentoEmpenho) => {
        setSelectedDocumento(documento);
        setShowDeleteModal(true);
    };

    const handleDownloadFile = async (anexoId: number, nomeArquivoSalvo: string) => {
        try {
            await apiService.downloadArquivoAnexoEmpenho(anexoId, nomeArquivoSalvo);
            toast.success("Download iniciado com sucesso");
        } catch (error) {
            console.error("Erro ao fazer download do arquivo:", error);
            toast.error("Erro ao fazer download do arquivo");
        }
    };

    const confirmDelete = async () => {
        if (!selectedDocumento) return;

        try {
            await apiService.deleteAnexoEmpenho(selectedDocumento.id);
            toast.success("Documento excluído com sucesso!");
            loadData();
            onUpdateComplete(); // Recarregar dados do contrato
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Erro ao excluir documento:", error);
            toast.error("Erro ao excluir documento");
        }
    };

    const handleDocumentoSaved = () => {
        loadData();
        onUpdateComplete();
        setShowAddModal(false);
        setShowEditModal(false);
    };

    return (
        <>
            <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                    <h4 className="fw-semibold">Documentos de Empenhos</h4>
                    <div>
                        <button
                            className="btn btn-primary"
                            onClick={handleAddDocumento}
                        >
                            <i className="ti ti-plus me-1"></i>
                            Adicionar Documento
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="text-center p-3">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : documentos.length === 0 ? (
                        <div className="alert alert-info">
                            Nenhum documento de empenho registrado para este contrato.
                        </div>
                    ) : (
                        <div className="notes-activity">
                            {documentos.map((doc) => (
                                <div className="card mb-3" key={doc.id}>
                                    <div className="card-body">
                                        <div className="d-flex align-items-center justify-content-between pb-2">
                                            <div className="d-inline-flex align-items-center mb-2">
                                                <div>
                                                    <h5 className="fw-medium mb-1">{doc.titulo}</h5>
                                                    <p className="mb-0">
                                                        {new Date(doc.data_upload).toLocaleDateString('pt-BR', {
                                                            day: '2-digit',
                                                            month: 'short',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="mb-2">

                                                <Link
                                                    className="dropdown-item"
                                                    to="#"
                                                    onClick={() => handleEditDocumento(doc)}
                                                >
                                                    <i className="ti ti-edit me-1"></i> Editar
                                                </Link>
                                                <Link
                                                    className="dropdown-item"
                                                    to="#"
                                                    onClick={() => handleDeleteDocumento(doc)}
                                                >
                                                    <i className="ti ti-trash me-1"></i> Excluir
                                                </Link>

                                            </div>
                                        </div>

                                        <p><strong>Empenho:</strong> {doc.numero_empenho || `EMP-${doc.id_empenho}`}</p>

                                        {doc.arquivos && Array.isArray(doc.arquivos) && doc.arquivos.length > 0 && (
                                            <div className="d-inline-flex align-items-center flex-wrap">
                                                {doc.arquivos.map((arquivo, index) => (
                                                    <div className="note-download me-3" key={index}>
                                                        <div className="note-info">
                                                            <span className="note-icon bg-secondary-success">
                                                                <i className={`ti ti-${getFileIcon(arquivo.tipo)}`} />
                                                            </span>
                                                            <div>
                                                                <h6 className="fw-medium mb-1">
                                                                    {arquivo.nome_original}
                                                                </h6>
                                                                <p>{formatFileSize(arquivo.tamanho)}</p>
                                                            </div>
                                                        </div>
                                                        <Link
                                                            to="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleDownloadFile(doc.id, arquivo.nome_salvo);
                                                            }}
                                                        >
                                                            <i className="ti ti-arrow-down" />
                                                        </Link>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal para adicionar documento */}
            <ModalDocumentoEmpenho
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                contratoId={contratoId}
                empenhos={empenhos}
                documento={null}
                onSave={handleDocumentoSaved}
            />

            {/* Modal para editar documento */}
            {selectedDocumento && (
                <ModalDocumentoEmpenho
                    show={showEditModal}
                    onHide={() => setShowEditModal(false)}
                    contratoId={contratoId}
                    empenhos={empenhos}
                    documento={selectedDocumento}
                    onSave={handleDocumentoSaved}
                />
            )}

            {/* Confirm Delete Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <div className="modal-body">
                    <div className="text-center">
                        <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                            <i className="ti ti-trash-x fs-36 text-danger" />
                        </div>
                        <h4 className="mb-2">Excluir Documento?</h4>
                        <p className="mb-0">
                            Tem certeza que deseja excluir o documento <br />
                            <strong>{selectedDocumento?.titulo}</strong>?
                        </p>
                        <div className="d-flex align-items-center justify-content-center mt-4">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={confirmDelete}
                            >
                                Sim, Excluir
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default DocumentosEmpenho;